import { SiteUrls } from '@tableau/shared-urls';
import { TextAreaWidget } from '@tableau/widgets';
import * as React from 'react';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { CommonStyles } from '../../../../common/react/CommonStyles';
import { cssClass } from '../../../../style';
import { updateDataroleApi } from '../../../apis/UpdateDatarole';
import { sectionHeaderClass, sectionTitleClass } from '../../../styles/DefinitionSubspaceSharedStyles';
import { ConceptName } from '../../../types/ConceptName';
import { DataType } from '../../../types/DetailEnums';
import { EnumChipPicker } from '../../../utils/chip-pickers/EnumChipPicker';
import { SynonymsChipPicker } from '../../../utils/chip-pickers/SynonymChipPicker';
import { friendlyDataType, getFriendlyDataType } from '../../../utils/contentMaps';
import { DescriptorBlock, DescriptorBlockChildProps } from './DescriptorBlock';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { ServiceLocator } from '../../../../app/ServiceLocator';


export interface DefinitionSubspaceAboutSectionProps {
  readonly siteUrls: SiteUrls;
  readonly luid: string;
  readonly dataTypes: ReadonlyArray<DataType>;
  readonly description: string;
  readonly synonyms: ReadonlyArray<ConceptName>;
}

const cssPrefix = 'DefinitionSubspaceAboutSection';
const rowClass = cssClass(cssPrefix, 'topRow', {display: 'flex', flexWrap: 'wrap', paddingLeft: CommonStyles.Sizes.DefaultSpace});
const descriptionEdit = cssClass(cssPrefix, 'descriptionEdit', {
  width: '100%',
  height: '6em',
  $nest: {
    'textarea': {
      resize: 'none',
      width: '100%',
      height: '100%',
    }
  }});

export const DefinitionSubspaceAboutSection: React.StatelessComponent<DefinitionSubspaceAboutSectionProps> = (props: DefinitionSubspaceAboutSectionProps) => {
  const toasterService: ToasterService = ServiceLocator.get<ToasterService>('ToasterService');

  return <section>
    <header className={sectionHeaderClass}><h3 className={sectionTitleClass}>{Messages.dataRoleDefinitionSubspace_About_Title()}</h3></header>
    <div className={rowClass} >
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_RoleType()}
        content={Messages.dataRoleDefinitionSubspace_About_RoleType_Dictionary()}/>
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_DataType()}
        content={props.dataTypes}
        contentDisplay={displayDataTypes}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_DataType_None()}
        />
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms()}
        content={props.synonyms}
        contentDisplay={displayFieldSynonyms}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_None()}
        onSave={saveSynonymsEdit(props.luid, toasterService)}
        editable
        />
    </div>
    <div className={rowClass} >
      <DescriptorBlock
        title={Messages.dataRoleDefinitionSubspace_About_Description()}
        content={props.description}
        contentDisplay={displayDescription}
        emptyMessage={Messages.dataRoleDefinitionSubspace_About_Description_None()}
        onSave={saveDescriptionEdit(props.luid, toasterService)}
        containerStyle={{flexGrow:1, maxWidth: '800px'}}
        editable
        />
    </div>
  </section>;
};

const displayDataTypes = (props: DescriptorBlockChildProps<DataType[]>) => {
  if (!props.isEditing) { return props.value.length > 0 ? <>{props.value.map(getFriendlyDataType).join(', ')}</> : null; }
  return <EnumChipPicker
    placeholder={Messages.dataRoleDefinitionSubspace_About_DataType_Placeholder()}
    testId="data-type-chip-picker"
    values={props.value}
    enumValues={Object.keys(friendlyDataType())}
    friendlyNames={friendlyDataType()}
    onChange={props.onChange}
    label={Messages.dataRoleDefinitionSubspace_About_DataType()}
    style={{maxWidth: '300px'}}/>;
};

const displayFieldSynonyms = (props: DescriptorBlockChildProps<ConceptName[]>): React.ReactElement => {
  const synonyms = props.value.filter(name => !name.is_primary);
  const primaryName = props.value.filter(name => name.is_primary);

  if (!props.isEditing) { return synonyms.length > 0 ? <>{synonyms.map(x => x.name).join(', ')}</> : null; }
  return <SynonymsChipPicker
    placeholder={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_Placeholder()}
    testId="field-synonym-chip-picker"
    values={synonyms}
    onChange={(newNames: ConceptName[]) => props.onChange([...primaryName, ...newNames])}
    label={Messages.dataRoleDefinitionSubspace_About_FieldSynonyms()}
    style={{maxWidth: '300px'}}
    getErrorMessage={validateSynonym}/>;
};

const displayDescription = (props: DescriptorBlockChildProps<string>) => {
  if (!props.isEditing) { return props.value && <>{props.value}</>; }
  return <TextAreaWidget
    testId="description-edit"
    label={undefined}
    maxLength={4000}
    onChange={(e) => props.onChange(e.target.value)}
    defaultValue={props.value}
    className={descriptionEdit}/>;
};

const saveDescriptionEdit = (luid: string, toasterService: ToasterService) => (newDescription: string) => {
  updateDataroleApi(luid, {description: newDescription})
    .catch(() => toasterService.pop({
      type: ToastType.Error,
      text: Messages.dataRoleDefinitionSubspace_About_ToastError_Description(),
    }));
};

const saveSynonymsEdit = (luid: string, toasterService: ToasterService) => (names: ConceptName[]) => {
  updateDataroleApi(luid, {
    field_concept:{ names },
  })
  .catch(() => toasterService.pop({
    type: ToastType.Error,
    text: Messages.dataRoleDefinitionSubspace_About_ToastError_Synonyms(),
  }));
};

const validateSynonym = (synonym: string): string => {
  if (synonym.length > 255) { return Messages.dataRoleDefinitionSubspace_About_FieldSynonyms_Error_TooLong({MAX_LENGTH: Formatters.formatCount(255) }); }
  return '';
};
